import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { combineLatest, of } from "rxjs";
import {
    catchError,
    exhaustMap,
    map,
    mergeMap,
    switchMap,
} from "rxjs/operators";

import { DeleteAssetDialogComponent } from "#components/dialog-boxes/delete-asset-dialog/delete-asset-dialog.component";
import { GenericDialogComponent } from "#components/dialog-boxes/generic-dialog/generic-dialog.component";
import { LocalStorage, SnackBarService } from "#core/services";
import { MEDIA_UPDATED } from "#core/string-substitutions/avenue-asset";
import { AlgoliaSearchClientHelper } from "#helpers/algolia-search-client.helper";
import { MatDialog } from "@angular/material/dialog";
import { Asset, Bundle, PerformMedia, assetTypeIds } from "@carnegie-learning/cure-types";
import { AssetsService } from "@carnegie-learning/data-access-angular-cure";
import { AlgoliaSyncService, UpdatedAssetMapService } from "@carnegie-learning/data-access-angular-passport";
import moment from "moment";
import * as assetsActions from "../actions/assets.actions";

@Injectable()
export class AssetsEffects {
    constructor(
        private actions$: Actions,
        private assetsService: AssetsService,
        private algoliaSyncService: AlgoliaSyncService,
        private snackBarService: SnackBarService,
        private localStorage: LocalStorage,
        private algoliaSearchClientHelper: AlgoliaSearchClientHelper,
        private updatedAssetMapService: UpdatedAssetMapService,
        public dialog: MatDialog
    ) {}

    loadAssets$ = createEffect(() =>
        this.actions$.pipe(ofType(assetsActions.LoadAssets)).pipe(
            switchMap((action) => {
                return this.assetsService
                    .getAssets(action.paginationParams)
                    .pipe(
                        map((assets) => assets),
                        map((assets) =>
                            assetsActions.LoadAssetsSuccess({ payload: assets })
                        ),
                        catchError((error) =>
                            of(assetsActions.LoadAssetsFail({ payload: error }))
                        )
                    );
            })
        )
    );

    loadAssetsFromIds$ = createEffect(() =>
        this.actions$.pipe(ofType(assetsActions.LoadAssetsFromIds)).pipe(
            switchMap((action) => {
                return this.assetsService
                    .getAssetsFromIds(action.assetIds, 1000, false)
                    .pipe(
                        map((assets) => assets),
                        map((assets) =>
                            assetsActions.LoadAssetsFromIdsSuccess({
                                payload: assets,
                            })
                        ),
                        catchError((error) =>
                            of(
                                assetsActions.LoadAssetsFromIdsFail({
                                    payload: error,
                                })
                            )
                        )
                    );
            })
        )
    );

    loadSelectedAsset$ = createEffect(() =>
        this.actions$.pipe(ofType(assetsActions.LoadAsset)).pipe(
            switchMap((payload) => {
                return this.assetsService
                    .getSelectedAsset(payload.assetId)
                    .pipe(
                        map((response) =>
                            assetsActions.LoadAssetSuccess({
                                payload: response,
                            })
                        ),
                        catchError((error) =>
                            of(assetsActions.LoadAssetFail({ payload: error }))
                        )
                    );
            })
        )
    );

    createAsset$ = createEffect(() =>
        this.actions$.pipe(ofType(assetsActions.createAsset)).pipe(
            switchMap((action) => {
                return this.assetsService.createAsset(action.payload).pipe(
                    mergeMap((newAsset) => {
                        if (
                            newAsset.assetTypeId === assetTypeIds.PERFORM &&
                            !newAsset.isAssignable &&
                            !newAsset.isResource
                        ) {
                            this.snackBarService.success(
                                "Media Added To Library!"
                            );
                            if (newAsset.assetId) {
                                this.updatedAssetMapService.updateMap(
                                    newAsset.assetId,
                                    <PerformMedia>newAsset
                                );
                            }
                            return of(
                                assetsActions.createAssetSuccess({
                                    payload: newAsset,
                                    createAdd: action.createAdd,
                                    createAddBundle: action.createAddBundle,
                                })
                            );
                        }
                        return this.algoliaSyncService
                            .createAlgoliaAsset([newAsset])
                            .pipe(
                                map((response) => { 
                                    let successMsg = "Activity Created";
                                    // If it's of type perform, and it's not assignable and its not a resource, it's perform media.
                                    successMsg =
                                        newAsset.assetTypeId ===
                                        assetTypeIds.BUNDLE
                                            ? "Bundle Created"
                                            : successMsg;
                                    if (action.createAdd) {
                                        successMsg +=
                                            " And Added To Your Cart!";
                                    } else {
                                        successMsg += "!";
                                    }
                                    this.snackBarService.success(successMsg);
                                    // Algolia needs to clear Cache to update the created asset, and stop using the local saved data
                                    this.algoliaSearchClientHelper
                                        .getClient()
                                        .clearCache();

                                    return assetsActions.createAssetSuccess({
                                        payload: newAsset,
                                        createAdd: action.createAdd,
                                        createAddBundle: action.createAddBundle,
                                    }); 
                                
                                }),
                                catchError((error) => {
                                    const errorMsg = "The bundle you are creating exceeds the maximum bundle size. Please try creating the bundle in two parts. If you need further assistance, please contact Support.";
                                    // If an error is caught, show the user errorMsg
                                    this.snackBarService.error(errorMsg);
                                    return of(assetsActions.createAssetFail({
                                        payload: error,
                                    }));
                                })
                            );
                        }),
                        catchError((error) =>
                            of(
                                assetsActions.createAssetFail({
                                    payload: error,
                                })
                            )
                        )
                    );
                })
            )
        );  

    updateAsset$ = createEffect(() =>
        this.actions$.pipe(ofType(assetsActions.updateAsset)).pipe(
            map((action) => action.payload),
            switchMap((asset) => {
                return this.assetsService.updateAsset(asset).pipe(
                    mergeMap((updatedAsset) => {
                        return this.algoliaSyncService
                            .updateAlgoliaAsset([updatedAsset])
                            .pipe(
                                map((msg) => {
                                    let successMsg =
                                        updatedAsset.assetTypeId ===
                                            assetTypeIds.PERFORM &&
                                        !updatedAsset.isAssignable &&
                                        !updatedAsset.isResource
                                            ? MEDIA_UPDATED
                                            : "Activity Updated!";
                                    // If it's of type perform, and it's not assignable and its not a resource, it's perform media.
                                    successMsg =
                                        updatedAsset.assetTypeId ===
                                        assetTypeIds.BUNDLE
                                            ? "Bundle Updated!"
                                            : successMsg;
                                    this.snackBarService.success(successMsg);
                                    // clear algolia's cache to update the created asset and stop using the local saved data
                                    this.algoliaSearchClientHelper
                                        .getClient()
                                        .clearCache();
                                    return assetsActions.updateAssetSuccess({
                                        payload: updatedAsset,
                                    });
                                })
                            );
                    }),
                    catchError((error) =>
                        of(
                            assetsActions.updateAssetFail({
                                payload: error,
                            })
                        )
                    )
                );
            })
        )
    );

    removeAsset$ = createEffect(() =>
        this.actions$.pipe(ofType(assetsActions.removeAsset)).pipe(
            switchMap((action) => {
                return this.assetsService
                    .removeAsset(action.payload.assetId)
                    .pipe(
                        mergeMap((deletedAsset) => {
                            return this.algoliaSyncService
                                .removeAlgoliaAsset(deletedAsset.assetId)
                                .pipe(
                                    map((asset) => {
                                        const successMsg =
                                            deletedAsset.assetTypeId ===
                                                assetTypeIds.PERFORM &&
                                            !deletedAsset.isAssignable &&
                                            !deletedAsset.isResource
                                                ? "Media Deleted From Library!"
                                                : "Activity Deleted!";
                                        this.snackBarService.success(
                                            successMsg
                                        );
                                        // Algolia needs to clear Cache to update the removed asset, and stop using the local saved data
                                        this.algoliaSearchClientHelper
                                            .getClient()
                                            .clearCache();
                                        return assetsActions.removeAssetSuccess(
                                            {
                                                payload: action.payload,
                                            }
                                        );
                                    })
                                );
                        }),
                        catchError((error) => {
                            return of(
                                assetsActions.removeAssetFail({
                                    payload: error,
                                })
                            );
                        })
                    );
            })
        )
    );

    LoadDistinctFilterDefinitions$ = createEffect(() =>
        this.actions$
            .pipe(ofType(assetsActions.LoadDistinctFiltersDefinitions))
            .pipe(
                mergeMap(({ params, filterAttributes }) => {
                    const keysArr: Array<string[]> = [];
                    const attributes: string[] = [];
                    filterAttributes.forEach((filter) => {
                        const attribute = filter.attribute;
                        // For assetTypeId and assetActivityTypeId, the filter needs to be a join of both fields
                        const keys =
                            attribute === "assetTypeId"
                                ? ["assetTypeId", "assetActivityTypeId"]
                                : [attribute];
                        keysArr.push(keys);
                        attributes.push(attribute);
                    });

                    return combineLatest([
                        ...keysArr.map((keys) =>
                            this.assetsService.getDistinctFilterDefinitions(
                                params,
                                keys
                            )
                        ),
                    ]).pipe(
                        map((responses: Array<string[]>) => {
                            return assetsActions.LoadDistinctFilterDefinitionsSuccess(
                                {
                                    attributes,
                                    payload: responses,
                                }
                            );
                        }),
                        catchError((error) =>
                            of(
                                assetsActions.LoadDistinctFilterDefinitionsFail(
                                    {
                                        payload: error,
                                    }
                                )
                            )
                        )
                    );
                })
            )
    );

    LoadDistinctPublishedByFilter$ = createEffect(() =>
        this.actions$
            .pipe(ofType(assetsActions.LoadDistinctPublishedByFilter))
            .pipe(
                mergeMap(
                    ({
                        resourcesTabActive,
                        assignablesTabActive,
                        editionId,
                    }) => {
                        const keys: string[] = [
                            "school",
                            "district",
                            "none",
                            "global",
                        ];

                        return combineLatest([
                            ...keys.map((key) => {
                                const params = {
                                    visibility: key,
                                    isResource: resourcesTabActive,
                                    isAssignable: assignablesTabActive,
                                    editionIds: [editionId],
                                };
                                return this.assetsService.getDistinctFilterDefinitions(
                                    params,
                                    ["publishedBy"]
                                );
                            }),
                        ]).pipe(
                            map((responses: Array<string[]>) => {
                                return assetsActions.LoadDistinctPublishedByFilterSuccess(
                                    {
                                        keys: keys,
                                        payload: responses,
                                    }
                                );
                            }),
                            catchError((error) =>
                                of(
                                    assetsActions.LoadDistinctPublishedByFilterFail(
                                        {
                                            payload: error,
                                        }
                                    )
                                )
                            )
                        );
                    }
                )
            )
    );

    loadBundlesTasks$ = createEffect(() =>
        this.actions$.pipe(ofType(assetsActions.loadBundlesTasks)).pipe(
            exhaustMap((action) => {
                const assets = action.payload;

                const bundles = (<Bundle[]>assets).filter(
                    (asset) => asset.assetTypeId === assetTypeIds.BUNDLE
                );
                const assetIdsArray = bundles.map(
                    (bundle) => bundle.bundledAssetIds
                );
                const bundleAssetIdArray = bundles.map(
                    (bundle) => bundle.assetId
                );

                return combineLatest([
                    ...assetIdsArray.map((ids) =>
                        this.assetsService.getAssetsFromIds(ids, 1000, false)
                    ),
                ]).pipe(
                    map((resArr) => {
                        const updatedAssets: (Asset | Bundle)[] = [];
                        assets.forEach((asset) => {
                            const i = bundleAssetIdArray.findIndex(
                                (assetId) => assetId === asset.assetId
                            );
                            if (asset.assetTypeId === assetTypeIds.BUNDLE) {
                                const tasks = resArr[i].data.sort((a, b) => {
                                    return (
                                        (<Bundle>asset).bundledAssetIds.indexOf(
                                            a.assetId
                                        ) -
                                        (<Bundle>asset).bundledAssetIds.indexOf(
                                            b.assetId
                                        )
                                    );
                                });
                                const updatedbundle: Bundle = {
                                    ...(<Bundle>asset),
                                    tasks,
                                };
                                updatedAssets.push(updatedbundle);
                            } else {
                                updatedAssets.push(asset);
                            }
                        });
                        return assetsActions.loadBundlesTasksSuccess({
                            payload: updatedAssets,
                        });
                    }),
                    catchError((error) =>
                        of(
                            assetsActions.loadBundlesTasksFail({
                                payload: error,
                            }),
                            // even if loading bundle fail, we should still list the assets.
                            assetsActions.loadedAssetsFromAlgolia({
                                payload: assets,
                            })
                        )
                    )
                );
            })
        )
    );

    cloneAsset$ = createEffect(() =>
        this.actions$.pipe(ofType(assetsActions.cloneAsset)).pipe(
            map((action) => action.payload),
            switchMap((asset) => {
                const userProfile = this.localStorage.get("userProfile");
                asset = {
                    ...asset,
                    publishedBy: userProfile.userGUID,
                    audit: {
                        createdBy: userProfile.userGUID,
                        createdAt: moment().format("MM/DD/YYYY HH:mm:ss"),
                    },
                };
                if (!asset.visibility || !asset.isResource) {
                    asset = {
                        ...asset,
                        visibility: "self",
                        isResource: false,
                    };
                }

                if (asset.externalId || asset.linkId) {
                    // CURE-408 When an asset is copied , the externalId and linkId should not be added the copy. These are fields to be used by ONLY "CL" published assets.
                    delete asset.externalId;
                    delete asset.linkId;
                }

                return this.assetsService.cloneAsset(asset).pipe(
                    mergeMap((newAsset) => {
                        if (newAsset.audit.updatedAt !== undefined) {
                            this.snackBarService.success(
                                "Assignable Already Cloned!"
                            );
                            return of(
                                assetsActions.cloneAssetFail({
                                    payload: "Assignable Already Cloned!",
                                })
                            );
                        }
                        return this.algoliaSyncService
                            .createAlgoliaAsset([newAsset])
                            .pipe(
                                map((assets) => {
                                    // Algolia needs to clear Cache to update the created asset, and stop using the local saved data
                                    this.algoliaSearchClientHelper
                                        .getClient()
                                        .clearCache();
                                    if (
                                        newAsset.assetTypeId ===
                                        assetTypeIds.BUNDLE
                                    ) {
                                        this.snackBarService.success(
                                            "Bundle Cloned!"
                                        );
                                    } else {
                                        this.snackBarService.success(
                                            "Activity Cloned!"
                                        );
                                    }
                                    return assetsActions.cloneAssetSuccess({
                                        payload: newAsset,
                                    });
                                })
                            );
                    }),
                    catchError((error) =>
                        of(
                            assetsActions.cloneAssetFail({
                                payload: error,
                            })
                        )
                    )
                );
            })
        )
    );

    openedDeleteAssetDialog$ = createEffect(() =>
        this.actions$.pipe(ofType(assetsActions.openedDeleteAssetDialog)).pipe(
            map((action) => action.asset),
            switchMap((asset) => {
                return this.dialog
                    .open(DeleteAssetDialogComponent, {
                        width: "40vw",
                        maxWidth: "700px",
                        minWidth: "300px",
                        data: {
                            asset,
                        },
                    })
                    .afterClosed()
                    .pipe(
                        switchMap((selectedAction) => {
                            if (selectedAction) {
                                return [
                                    assetsActions.closedDeleteAssetDialog({
                                        selectedAction,
                                        asset,
                                    }),
                                    assetsActions.removeAsset({
                                        payload: asset,
                                    }),
                                ];
                            } else {
                                return of(
                                    assetsActions.closedDeleteAssetDialog({
                                        selectedAction,
                                        asset,
                                    })
                                );
                            }
                        })
                    );
            })
        )
    );

    openedEditAssignedBundleWarningDialog$ = createEffect(() =>
        this.actions$
            .pipe(ofType(assetsActions.openedEditAssignedBundleWarningDialog))
            .pipe(
                map((action) => action.asset),
                switchMap(() => {
                    return this.dialog
                        .open(GenericDialogComponent, {
                            width: "40vw",
                            maxWidth: "700px",
                            minWidth: "300px",
                            data: {
                                genericTitle: "Warning",
                                genericContent:
                                    "This activity has been assigned to students. You cannot edit an activity once it has been assigned.",
                            },
                        })
                        .afterClosed()
                        .pipe(
                            map(() =>
                                assetsActions.closedEditAssignedBundleWarningDialog()
                            )
                        );
                })
            )
    );
}
